import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient, UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { notifyUnauthorized } from "../app/authEvents.js";
import { useSession } from "../app/session.js";
import { claimWithInvite, ensureOrgKey, createTeam, decryptTeamPayload, encryptTeamPayload, inviteMember, joinWithInvite, previewInvite, rotateTeamKey } from "../app/teamKeys.js";
import type { InviteAccepted } from "../app/teamKeys.js";
import {
  acceptPendingInvite,
  createCollection,
  createTeamItem,
  declineInvite,
  deleteCollection,
  deleteOrg,
  deleteTeamItem,
  fetchOrg,
  listAudit,
  listCollectionItems,
  listCollections,
  listInvites,
  listOrgMembers,
  listOrgs,
  listPendingInvites,
  patchOrg,
  patchTeamItem,
  removeMember,
  renameCollection,
  restoreTeamItem,
  revokeInvite,
  upsertMember
} from "../api/teams.js";
import type {
  AuditEventView,
  InviteLookup,
  InviteView,
  OrgDetail,
  OrgMemberView,
  OrgSummary,
  PendingInvite,
  TeamCollection,
  TeamItemFull,
  TeamItemMeta,
  TeamRole
} from "../api/teams.js";
import { ApiClientError } from "../api/http.js";
import type { CryptoClient } from "../cryptoClient.js";
import type { VaultPayload } from "../vaultPayload.js";

export interface TeamEntry {
  readonly meta: TeamItemMeta;
  readonly data: VaultPayload;
}

export interface TeamItemsLoad {
  readonly entries: ReadonlyArray<TeamEntry>;
  readonly skipped: number;
}

export interface TeamSaveInput {
  readonly id?: string | undefined;
  readonly collectionId: string;
  readonly version?: number | undefined;
  readonly folder: string;
  readonly favorite: boolean;
  readonly payload: VaultPayload;
}

const LOCKED = "Der Tresor ist gesperrt.";
const PENDING_INVITES_KEY = ["teams", "pending-invites"] as const;

function useActiveClient(): CryptoClient | undefined {
  const { state } = useSession();
  return state.status === "unlocked" ? state.client : undefined;
}

function useSelfEmail(): string | undefined {
  const { state } = useSession();
  return state.status === "unlocked" ? state.email : undefined;
}

function onMutationError(err: unknown): void {
  if (err instanceof ApiClientError && err.status === 401) {
    notifyUnauthorized();
  }
}

function stripped(item: TeamItemFull): TeamItemMeta {
  return {
    id: item.id,
    collectionId: item.collectionId,
    folder: item.folder,
    titleHash: item.titleHash,
    version: item.version,
    favorite: item.favorite,
    updatedAt: item.updatedAt,
    deletedAt: item.deletedAt
  };
}

function invalidateOrg(queryClient: QueryClient, orgId: string): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: ["teams", orgId] });
}

/**
 * Teammates change shared data, so re-read it when the team page opens or the window regains focus.
 * No interval polling: every item listing writes a `collection_opened` audit event.
 */
const SHARED_FRESHNESS: { readonly refetchOnMount: "always"; readonly refetchOnWindowFocus: boolean } = {
  refetchOnMount: "always",
  refetchOnWindowFocus: true
};

export function useOrgs(): UseQueryResult<OrgSummary[], Error> {
  const client = useActiveClient();
  return useQuery<OrgSummary[], Error>({
    queryKey: ["teams", "list"],
    queryFn: listOrgs,
    enabled: client !== undefined
  });
}

export function useOrg(id: string): UseQueryResult<OrgDetail, Error> {
  const client = useActiveClient();
  return useQuery<OrgDetail, Error>({
    queryKey: ["teams", id, "detail"],
    queryFn: (): Promise<OrgDetail> => fetchOrg(id),
    enabled: client !== undefined
  });
}

/** Loads the org key into the worker. Items are only queried after this succeeds. */
export function useOrgKeyReady(org: OrgDetail | undefined): UseQueryResult<boolean, Error> {
  const client = useActiveClient();
  return useQuery<boolean, Error>({
    queryKey: org === undefined ? ["teams", "none", "key"] : ["teams", org.id, "key", org.keyVersion],
    queryFn: async (): Promise<boolean> => {
      if (client === undefined || org === undefined) {
        throw new Error(LOCKED);
      }
      await ensureOrgKey(client, org);
      return true;
    },
    enabled: client !== undefined && org !== undefined,
    staleTime: Number.POSITIVE_INFINITY
  });
}

export function useOrgMembers(id: string): UseQueryResult<OrgMemberView[], Error> {
  const client = useActiveClient();
  return useQuery<OrgMemberView[], Error>({
    queryKey: ["teams", id, "members"],
    queryFn: (): Promise<OrgMemberView[]> => listOrgMembers(id),
    enabled: client !== undefined
  });
}

export function useCollections(id: string): UseQueryResult<TeamCollection[], Error> {
  const client = useActiveClient();
  return useQuery<TeamCollection[], Error>({
    queryKey: ["teams", id, "collections"],
    queryFn: (): Promise<TeamCollection[]> => listCollections(id),
    enabled: client !== undefined,
    ...SHARED_FRESHNESS
  });
}

export function useAudit(id: string): UseQueryResult<AuditEventView[], Error> {
  const client = useActiveClient();
  return useQuery<AuditEventView[], Error>({
    queryKey: ["teams", id, "audit"],
    queryFn: (): Promise<AuditEventView[]> => listAudit(id),
    enabled: client !== undefined
  });
}

export function useInvites(id: string, enabled: boolean): UseQueryResult<InviteView[], Error> {
  const client = useActiveClient();
  return useQuery<InviteView[], Error>({
    queryKey: ["teams", id, "invites"],
    queryFn: (): Promise<InviteView[]> => listInvites(id),
    enabled: client !== undefined && enabled
  });
}

/** Decrypts every item of every collection. Trashed items are included, marked by meta.deletedAt. */
export function useTeamItems(
  org: OrgDetail | undefined,
  collections: ReadonlyArray<TeamCollection> | undefined,
  keyReady: boolean
): UseQueryResult<TeamItemsLoad, Error> {
  const client = useActiveClient();
  const collectionIds = collections === undefined ? "" : collections.map((c: TeamCollection): string => c.id).join(",");
  return useQuery<TeamItemsLoad, Error>({
    queryKey: org === undefined ? ["teams", "none", "items"] : ["teams", org.id, "items", org.keyVersion, collectionIds],
    queryFn: async (): Promise<TeamItemsLoad> => {
      if (client === undefined || org === undefined || collections === undefined) {
        throw new Error(LOCKED);
      }
      const entries: TeamEntry[] = [];
      let skipped = 0;
      for (const collection of collections) {
        const items = await listCollectionItems(collection.id);
        for (const item of items) {
          try {
            const data = await decryptTeamPayload(client, org.id, item);
            if (data === undefined) {
              skipped += 1;
            } else {
              entries.push({ meta: stripped(item), data });
            }
          } catch {
            skipped += 1;
          }
        }
      }
      return { entries, skipped };
    },
    enabled: client !== undefined && org !== undefined && collections !== undefined && keyReady,
    ...SHARED_FRESHNESS
  });
}

export function useCreateTeam(): UseMutationResult<string, Error, string> {
  const client = useActiveClient();
  const queryClient = useQueryClient();
  return useMutation<string, Error, string>({
    mutationFn: async (name: string): Promise<string> => {
      if (client === undefined) {
        throw new Error(LOCKED);
      }
      const orgId = await createTeam(client, name);
      // Items always live in a collection; seed one so a fresh team is usable right away.
      await createCollection(orgId, "Allgemein");
      return orgId;
    },
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: ["teams", "list"] });
    },
    onError: onMutationError
  });
}

export function useSaveTeamItem(org: OrgDetail): UseMutationResult<TeamEntry, Error, TeamSaveInput> {
  const client = useActiveClient();
  const queryClient = useQueryClient();
  return useMutation<TeamEntry, Error, TeamSaveInput>({
    mutationFn: async (input: TeamSaveInput): Promise<TeamEntry> => {
      if (client === undefined) {
        throw new Error(LOCKED);
      }
      const folder = input.folder.trim() === "" ? "Allgemein" : input.folder.trim();
      if (input.id === undefined) {
        const id = crypto.randomUUID();
        const sealed = await encryptTeamPayload(client, org.id, id, input.payload);
        const meta = await createTeamItem(input.collectionId, id, {
          folder,
          titleHash: sealed.titleHash,
          ciphertextB64: sealed.ciphertextB64,
          nonceB64: sealed.nonceB64,
          favorite: input.favorite
        });
        return { meta, data: input.payload };
      }
      if (input.version === undefined) {
        throw new Error("Versionsnummer fehlt.");
      }
      const sealed = await encryptTeamPayload(client, org.id, input.id, input.payload);
      const full = await patchTeamItem(input.collectionId, input.id, {
        folder,
        titleHash: sealed.titleHash,
        ciphertextB64: sealed.ciphertextB64,
        nonceB64: sealed.nonceB64,
        favorite: input.favorite,
        expectedVersion: input.version
      });
      return { meta: stripped(full), data: input.payload };
    },
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: ["teams", org.id, "items"] });
    },
    onError: onMutationError
  });
}

export function useToggleTeamFavorite(orgId: string): UseMutationResult<void, Error, TeamEntry> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, TeamEntry>({
    mutationFn: async (entry: TeamEntry): Promise<void> => {
      await patchTeamItem(entry.meta.collectionId, entry.meta.id, {
        favorite: !entry.meta.favorite,
        expectedVersion: entry.meta.version
      });
    },
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: ["teams", orgId, "items"] });
    },
    onError: onMutationError
  });
}

export function useTrashTeamItem(orgId: string): UseMutationResult<TeamItemMeta, Error, { collectionId: string; itemId: string }> {
  const queryClient = useQueryClient();
  return useMutation<TeamItemMeta, Error, { collectionId: string; itemId: string }>({
    mutationFn: ({ collectionId, itemId }): Promise<TeamItemMeta> => deleteTeamItem(collectionId, itemId),
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: ["teams", orgId, "items"] });
    },
    onError: onMutationError
  });
}

export function useRestoreTeamItem(orgId: string): UseMutationResult<TeamItemMeta, Error, { collectionId: string; itemId: string }> {
  const queryClient = useQueryClient();
  return useMutation<TeamItemMeta, Error, { collectionId: string; itemId: string }>({
    mutationFn: ({ collectionId, itemId }): Promise<TeamItemMeta> => restoreTeamItem(collectionId, itemId),
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: ["teams", orgId, "items"] });
    },
    onError: onMutationError
  });
}

export function useUpdateOrg(orgId: string): UseMutationResult<void, Error, { name?: string; require2fa?: boolean }> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { name?: string; require2fa?: boolean }>({
    mutationFn: (patch): Promise<void> => patchOrg(orgId, patch),
    onSuccess: (): void => {
      void invalidateOrg(queryClient, orgId);
      void queryClient.invalidateQueries({ queryKey: ["teams", "list"] });
    },
    onError: onMutationError
  });
}

export function useDeleteOrg(orgId: string): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: (): Promise<void> => deleteOrg(orgId),
    onSuccess: (): void => {
      queryClient.removeQueries({ queryKey: ["teams", orgId] });
      void queryClient.invalidateQueries({ queryKey: ["teams", "list"] });
    },
    onError: onMutationError
  });
}

export function useCollectionMutations(orgId: string): {
  readonly create: UseMutationResult<{ id: string; name: string }, Error, string>;
  readonly rename: UseMutationResult<void, Error, { id: string; name: string }>;
  readonly remove: UseMutationResult<void, Error, string>;
} {
  const queryClient = useQueryClient();
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["teams", orgId, "collections"] });
    void queryClient.invalidateQueries({ queryKey: ["teams", orgId, "items"] });
  };
  const create = useMutation<{ id: string; name: string }, Error, string>({
    mutationFn: (name: string): Promise<{ id: string; name: string }> => createCollection(orgId, name),
    onSuccess: refresh,
    onError: onMutationError
  });
  const rename = useMutation<void, Error, { id: string; name: string }>({
    mutationFn: ({ id, name }): Promise<void> => renameCollection(id, name),
    onSuccess: refresh,
    onError: onMutationError
  });
  const remove = useMutation<void, Error, string>({
    mutationFn: (id: string): Promise<void> => deleteCollection(id),
    onSuccess: refresh,
    onError: onMutationError
  });
  return { create, rename, remove };
}

export function useChangeRole(orgId: string): UseMutationResult<void, Error, { userId: string; role: TeamRole }> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { userId: string; role: TeamRole }>({
    mutationFn: ({ userId, role }): Promise<void> => upsertMember(orgId, { userId, role }),
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: ["teams", orgId, "members"] });
    },
    onError: onMutationError
  });
}

export interface RemoveResult {
  readonly rotated: boolean;
}

/** Removes a member, then re-keys the team with the remaining members. */
export function useRemoveMember(orgId: string): UseMutationResult<RemoveResult, Error, string> {
  const client = useActiveClient();
  const queryClient = useQueryClient();
  return useMutation<RemoveResult, Error, string>({
    mutationFn: async (userId: string): Promise<RemoveResult> => {
      await removeMember(orgId, userId);
      if (client === undefined) {
        return { rotated: false };
      }
      try {
        const fresh = await fetchOrg(orgId);
        await rotateTeamKey(client, fresh);
        return { rotated: true };
      } catch (err: unknown) {
        onMutationError(err);
        return { rotated: false };
      }
    },
    onSuccess: (): void => {
      void invalidateOrg(queryClient, orgId);
    },
    onError: onMutationError
  });
}

export function useLeaveTeam(orgId: string): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (userId: string): Promise<void> => removeMember(orgId, userId),
    onSuccess: (): void => {
      queryClient.removeQueries({ queryKey: ["teams", orgId] });
      void queryClient.invalidateQueries({ queryKey: ["teams", "list"] });
    },
    onError: onMutationError
  });
}

/** Re-keys the team on demand, for example after a failed rotation. */
export function useRotateTeamKey(orgId: string): UseMutationResult<number, Error, void> {
  const client = useActiveClient();
  const queryClient = useQueryClient();
  return useMutation<number, Error, void>({
    mutationFn: async (): Promise<number> => {
      if (client === undefined) {
        throw new Error(LOCKED);
      }
      return rotateTeamKey(client, await fetchOrg(orgId));
    },
    onSuccess: (): void => {
      void invalidateOrg(queryClient, orgId);
    },
    onError: onMutationError
  });
}

export interface InviteCreated {
  readonly secretB64: string;
  readonly email: string;
  readonly role: TeamRole;
  readonly expiresAt: number;
}

export function useCreateInvite(org: OrgDetail): UseMutationResult<InviteCreated, Error, { email: string; role: TeamRole }> {
  const client = useActiveClient();
  const queryClient = useQueryClient();
  return useMutation<InviteCreated, Error, { email: string; role: TeamRole }>({
    mutationFn: async (input): Promise<InviteCreated> => {
      if (client === undefined) {
        throw new Error(LOCKED);
      }
      return inviteMember(client, org, input.email, input.role);
    },
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: ["teams", org.id, "invites"] });
    },
    onError: onMutationError
  });
}

export function useRevokeInvite(orgId: string): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (inviteId: string): Promise<void> => revokeInvite(orgId, inviteId),
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: ["teams", orgId, "invites"] });
    },
    onError: onMutationError
  });
}

export function useInvitePreview(secretB64: string | undefined): UseQueryResult<InviteLookup, Error> {
  const client = useActiveClient();
  return useQuery<InviteLookup, Error>({
    queryKey: ["teams", "invite", secretB64 ?? ""],
    queryFn: async (): Promise<InviteLookup> => {
      if (client === undefined || secretB64 === undefined) {
        throw new Error(LOCKED);
      }
      return previewInvite(client, secretB64);
    },
    enabled: client !== undefined && secretB64 !== undefined,
    retry: false
  });
}

export function useJoinInvite(): UseMutationResult<InviteAccepted, Error, { secretB64: string; lookup: InviteLookup }> {
  const client = useActiveClient();
  const queryClient = useQueryClient();
  return useMutation<InviteAccepted, Error, { secretB64: string; lookup: InviteLookup }>({
    mutationFn: async ({ secretB64, lookup }): Promise<InviteAccepted> => {
      if (client === undefined) {
        throw new Error(LOCKED);
      }
      return joinWithInvite(client, secretB64, lookup);
    },
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: ["teams", "list"] });
      void queryClient.invalidateQueries({ queryKey: PENDING_INVITES_KEY });
    },
    onError: onMutationError
  });
}

/** Open invites for this account. Polled so an invite shows up without a reload. */
export function usePendingInvites(): UseQueryResult<PendingInvite[], Error> {
  const client = useActiveClient();
  return useQuery<PendingInvite[], Error>({
    queryKey: PENDING_INVITES_KEY,
    queryFn: listPendingInvites,
    enabled: client !== undefined,
    refetchInterval: 60_000
  });
}

/** Parks the invite on this account so leaving the page does not lose it. */
export function useClaimInvite(): UseMutationResult<void, Error, { secretB64: string; lookup: InviteLookup }> {
  const client = useActiveClient();
  const queryClient = useQueryClient();
  return useMutation<void, Error, { secretB64: string; lookup: InviteLookup }>({
    mutationFn: async ({ secretB64, lookup }): Promise<void> => {
      if (client === undefined) {
        throw new Error(LOCKED);
      }
      await claimWithInvite(client, secretB64, lookup);
    },
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: PENDING_INVITES_KEY });
    },
    onError: onMutationError
  });
}

export function useAcceptPendingInvite(): UseMutationResult<InviteAccepted, Error, PendingInvite> {
  const queryClient = useQueryClient();
  return useMutation<InviteAccepted, Error, PendingInvite>({
    mutationFn: async (invite): Promise<InviteAccepted> => {
      const accepted = await acceptPendingInvite(invite.id);
      return { orgId: accepted.orgId, orgName: invite.orgName, role: accepted.role };
    },
    onSettled: (): void => {
      void queryClient.invalidateQueries({ queryKey: ["teams", "list"] });
      void queryClient.invalidateQueries({ queryKey: PENDING_INVITES_KEY });
    },
    onError: onMutationError
  });
}

export function useDeclineInvite(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: declineInvite,
    onSettled: (): void => {
      void queryClient.invalidateQueries({ queryKey: PENDING_INVITES_KEY });
    },
    onError: onMutationError
  });
}

/** Email of the signed-in account, used to find our own membership row. */
export function useOwnMembership(members: ReadonlyArray<OrgMemberView> | undefined): OrgMemberView | undefined {
  const email = useSelfEmail();
  return members?.find((m: OrgMemberView): boolean => m.email !== null && m.email === email);
}
